import React from 'react';
import { makeCSS } from '../../../utils/makeCSS';
import { Typography } from '../../atoms/Typography';
import Truncate from '../../utility/Truncate';
import { Avatar } from '../../atoms/Avatar';
import Shimmer from '../../atoms/Loader/Shimmer';
import { AvatarGroup, AvatarGroupProps, UserInitial } from '../AvatarGroup';
import { Team } from '@cube3/common/model/schema/resources/team';
import { SizeVariants } from '../../../theme/themes';
import { SuggestionItemStatus } from './AccountSuggestionItem';
import { Key } from '../../atoms/Key';

export interface TeamSuggestionItemProps
  extends Pick<AvatarGroupProps, 'size' | 'maxCount'> {
  item: Team & SuggestionItemStatus;
  loading?: boolean;
  members?: UserInitial[];
  membersLength?: number;
  /**  Whether should show a modifier to indicate the shortcut for this option */
  showModifier?: boolean;
}

const useCSS = makeCSS(({ theme }) => ({
  root: {},
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  detailContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1
  },
  details: {
    flexGrow: 1,
    marginLeft: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  badge: {
    display: 'inline-block',
    padding: '0px 4px',
    backgroundColor: theme.color.overlay['03'],
    borderRadius: '8px',
    margin: '0 8px'
  },
  avatarList: { flexShrink: 0, justifySelf: 'flex-end' },
  modifier: {
    marginLeft: 8
  }
}));

export const TeamName = ({ teamName, suffix = '', disabled = false }) => {
  const classes = useCSS();
  return (
    <Typography colorVariant={disabled ? '03' : '01'} weight="body">
      <Truncate
        str={teamName}
        suffixComponent={
          suffix && (
            <span className={classes.badge}>
              <Typography
                colorVariant="02"
                weight="body"
                variant={SizeVariants.sm}
              >
                {suffix}
              </Typography>
            </span>
          )
        }
      />
    </Typography>
  );
};

export const TeamSuggestionItem = (props: TeamSuggestionItemProps) => {
  const {
    item,
    loading,
    members,
    maxCount,
    membersLength = undefined,
    size,
    showModifier
  } = props;

  const classes = useCSS();
  if (!item) return null;
  return (
    <div className={classes.row} onClick={item.onClick}>
      <div className={classes.detailContainer}>
        <Avatar
          userInitial={item.display_name}
          imageUrl={item.display_picture}
          avatarShape="square-shape"
        />
        <div className={classes.details}>
          <TeamName
            teamName={item.display_name}
            suffix="Team"
            disabled={item.disabled}
          />

          {membersLength !== undefined &&
            (loading ? (
              <div style={{ height: '1em', width: '90px' }}>
                <Shimmer />
              </div>
            ) : (
              <Typography
                colorVariant="02"
                weight="body"
              >{`${membersLength.toString()} ${
                membersLength === 1 ? 'member' : 'members'
              }`}</Typography>
            ))}
        </div>
      </div>

      <div className={classes.avatarList}>
        {loading ? (
          <div style={{ height: '2em' }}>
            <Shimmer />
          </div>
        ) : (
          members?.length > 0 && (
            <AvatarGroup users={members} maxCount={maxCount} size={size} />
          )
        )}
      </div>

      {item.disabled && item.disabledReason ? (
        <Typography colorVariant="03" weight="body" variant={SizeVariants.sm}>
          {item.disabledReason.toUpperCase()}
        </Typography>
      ) : null}

      {showModifier && item.active && !item.disabled ? (
        <div className={classes.modifier}>
          <Key
            keyCode={'Enter'}
            variant="modifier"
            modifierSize={SizeVariants.md}
          />
        </div>
      ) : null}
    </div>
  );
};
